import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function SaturationLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/saturation";
  const pageTitle = "Saturation in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what saturation is, how it adds harmonics, the difference between tape, tube, transistor, and digital saturation, and how saturation is used in mixing, mastering, and sound design.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "saturation",
          "audio saturation",
          "tape saturation",
          "tube saturation",
          "harmonic saturation",
          "mix saturation",
          "what is saturation in music",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Saturation" },
          { "@type": "Thing", name: "Harmonic distortion" },
          { "@type": "Thing", name: "Tape saturation" },
          { "@type": "Thing", name: "Tube saturation" },
          { "@type": "Thing", name: "Mixing" },
          { "@type": "Thing", name: "Mastering" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Saturation",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Saturation adds harmonic content and gentle nonlinear coloration, making sounds feel warmer, thicker, denser, smoother, or more forward.",
      icon: <ElectricBoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It can help a sound feel more alive, more present, and easier to hear in a mix without relying only on EQ or compression.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Vocals, drums, bass, guitars, synths, mix buses, mastering chains, and sound design of almost every kind.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const saturationTypes = [
    {
      name: "Tape Saturation",
      description:
        "Often associated with soft compression, rounded transients, subtle harmonic enhancement, and a sense of glue or smoothness.",
    },
    {
      name: "Tube Saturation",
      description:
        "Commonly described as warm, rich, smooth, and harmonically musical, often adding fullness and a slightly soft, flattering character.",
    },
    {
      name: "Transformer / Console Saturation",
      description:
        "Adds density, weight, and subtle color that can help sounds feel more substantial and mix elements feel more cohesive.",
    },
    {
      name: "Transistor Saturation",
      description:
        "Can sound punchy, focused, edgy, or tight depending on the circuit and how hard it is driven.",
    },
    {
      name: "Digital Saturation",
      description:
        "A broad category that can emulate analog behavior or create its own character, from subtle harmonic sweetening to more obvious clipping textures.",
    },
    {
      name: "Multiband Saturation",
      description:
        "Applies different saturation behavior to different frequency ranges, giving more control over warmth, presence, and bite.",
    },
  ];

  const harmonicConcepts = [
    {
      name: "Fundamental",
      description:
        "The original perceived pitch of a sound. Saturation does not usually replace it, but it builds on it.",
    },
    {
      name: "Harmonics",
      description:
        "Additional frequencies related to the fundamental. Saturation adds or emphasizes these harmonics, which changes timbre and character.",
    },
    {
      name: "Even Harmonics",
      description:
        "Often associated with smoother, warmer, sweeter coloration, though the real result depends on the source and circuit behavior.",
    },
    {
      name: "Odd Harmonics",
      description:
        "Often associated with stronger edge, aggression, bite, or intensity, especially as the processing becomes heavier.",
    },
  ];

  const controls = [
    {
      name: "Drive / Input",
      description:
        "Controls how hard the signal hits the saturation stage. More drive usually means more harmonics, more density, and more audible coloration.",
    },
    {
      name: "Output / Level",
      description:
        "Adjusts the signal level after saturation so you can level-match and judge the tonal change more accurately.",
    },
    {
      name: "Mix",
      description:
        "Blends the dry and saturated signals together, which is useful for parallel saturation and preserving transients or clarity.",
    },
    {
      name: "Tone / Bias / Character",
      description:
        "Shapes how the saturation reacts, often affecting brightness, harshness, low-end thickness, symmetry, or overall color.",
    },
    {
      name: "High-Pass / Low-Pass / Emphasis",
      description:
        "Lets you control which frequency ranges hit the saturation stage most strongly, which is extremely useful for tone shaping.",
    },
  ];

  const commonUses = [
    "Making vocals sound warmer, denser, and easier to hear without sounding obviously distorted",
    "Adding bite and presence to bass so it reads better on small speakers",
    "Giving drums more body, smack, and energy",
    "Helping guitars and synths feel more textured or more analog-like",
    "Adding glue and cohesion to buses or full mixes",
    "Creating lo-fi, vintage, gritty, or emotionally charged sound design textures",
  ];

  const bestPractices = [
    "Level-match carefully when judging saturation. Louder almost always sounds better, even when it is not actually better.",
    "Start subtle. Saturation often works best when you feel it before you clearly hear it.",
    "Use EQ before or after saturation to control what frequencies are being emphasized.",
    "Different saturation styles suit different material. Tape, tube, transistor, and digital clipping do not all react the same way.",
    "Check the result in context. A saturated track that sounds impressive in solo can become muddy, harsh, or crowded in the full mix.",
  ];

  const relatedLinks = [
    {
      title: "Distortion",
      to: "/learning/distortion",
      description:
        "Learn how broader distortion reshapes waveforms and creates more obvious harmonic aggression.",
    },
    {
      title: "Overdrive",
      to: "/learning/overdrive",
      description:
        "Explore the smoother, touch-sensitive side of gain and how it differs from heavier processing.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "See how EQ works alongside saturation to shape tone, clarity, and balance.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Understand how saturation and compression often overlap in feel, but not in exactly the same way.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Saturation | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Saturation in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Saturation is one of the most widely used forms of tone shaping in
                      music production, mixing, and sound design. It can be subtle or
                      obvious, smooth or gritty, warm or aggressive, but at its core,
                      saturation changes the shape of a signal in a nonlinear way and
                      adds harmonic content. That extra harmonic structure is what can
                      make a sound feel richer, thicker, more present, or more alive.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      In practical terms, saturation is often used to give sterile
                      digital recordings more character, help instruments cut through a
                      mix, soften transients in a flattering way, or add the feeling of
                      analog warmth and density. Sometimes it is barely noticeable on
                      its own. Other times it becomes a major part of the sound.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What is saturation in audio?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Saturation is a type of nonlinear processing that adds harmonics
                    and coloration to an audio signal. In many cases, it happens when
                    analog hardware or analog-style circuits are driven harder and
                    begin to respond less linearly. Instead of perfectly reproducing
                    the input, the system gently reshapes the waveform, which changes
                    the tone.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Compared with more aggressive distortion, saturation is often more
                    subtle and more focused on enhancement than destruction. It may
                    add thickness, smooth harsh transients, increase perceived
                    loudness, or create the impression of warmth and depth without
                    sounding obviously mangled. That is one reason saturation is so
                    common across modern mixing workflows.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    That said, saturation is still part of the broader distortion
                    family. The difference is often a matter of degree, style, and
                    intent. Light saturation can be almost invisible. Heavy saturation
                    can move toward unmistakable distortion.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      How saturation changes sound
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {harmonicConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    When people say saturation adds warmth or character, they are
                    usually describing the audible result of new harmonic content plus
                    subtle dynamic shaping. Saturation can make a bass line speak more
                    clearly, make a vocal sound more expensive, or make a drum bus
                    feel more exciting because the tone becomes denser and more
                    complex.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ElectricBoltRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common types of saturation
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {saturationTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Saturation vs distortion
                    </Typography>
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      ...subtlePanelSx,
                      p: 1.5,
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        They are related, but not always used in the same way
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Saturation is generally treated as the subtler, more
                        enhancement-focused side of distortion. It often aims to add
                        warmth, fullness, density, and harmonic richness without
                        sounding obviously aggressive.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Distortion usually implies a more dramatic reshaping of the
                        waveform. It is often harsher, more compressed, more
                        aggressive, and more clearly audible as an effect. The border
                        between the two is not strict, but saturation usually lives
                        closer to coloration, while distortion often lives closer to
                        effect.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common saturation controls
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How saturation is used in mixing and production
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      One of the biggest strengths of saturation is that it can improve
                      perceived presence without doing only one thing. EQ changes
                      frequency balance. Compression changes dynamics. Saturation can
                      affect both timbre and feel at the same time, which is why it is
                      so often used as a finishing touch or missing ingredient tool.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best practices for using saturation
                      </Typography>
                    </Stack>

                    <Stack spacing={1.15}>
                      {bestPractices.map((item, index) => (
                        <Stack key={index} direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Is saturation always subtle?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    No. Saturation is often subtle in mixing and mastering, but it can
                    also be pushed into very obvious coloration. At heavier settings,
                    saturation can create aggressive edge, crunch, flattening of peaks,
                    and obvious distortion-like textures. This is especially common in
                    creative sound design, drum processing, bass enhancement, and
                    stylized vocal production.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    In other words, saturation is not defined by being quiet or mild.
                    It is defined more by the style of nonlinear coloration and the
                    way people typically use it. Many tools let you move gradually
                    from gentle enhancement to stronger distortion without ever
                    leaving the same plugin.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Saturation is one of the most useful and musical ways to shape
                    tone. It can add warmth, presence, density, glue, edge, or
                    excitement depending on how it is applied. Sometimes it solves a
                    problem that EQ alone cannot fix. Sometimes it adds a finished,
                    analog-like quality that helps a mix feel more polished.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Whether you are working on vocals, guitars, drums, synths, buses,
                    or full mixes, learning how saturation behaves is worth the time.
                    Used carefully, it can be one of the most powerful tools in music
                    production because it changes not only what you hear, but how a
                    sound feels.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}